import * as React from "react";
import ReactPlayer from "react-player";
import { pushTagManagerEvent } from "sos-common/js/utils/helpers";
import {
  IVideoBlockProps,
  IVideoBlockState,
  ProgressState,
  VideoProvider,
} from "./VideoBlock.types";
import { isIOS } from "sos-common/js/utils/envCheck";
import Cookies from "js-cookie";
import EventService from "../../utils/EventService";
import * as CookieConsentConstants from "../CookieConsentManager/CookieConsentConstants";
/**
 * Displays an embed video from Youtube & Vimeo. react-player can handle more video sources but these two is enough for SOS.
 * The documentation of react-player is [here](https://www.npmjs.com/package/react-player).
 */
export class VideoBlock extends React.Component<
  IVideoBlockProps,
  IVideoBlockState
> {
  private _videoRef: ReactPlayer;
  private readonly _percentageStrings: string[];
  private _videoDuration: number;
  private statisticsCookieConsent: string;
  static defaultProps = {
    heading: null,
  } as IVideoBlockProps;

  constructor(props: IVideoBlockProps) {
    super(props);

    this._percentageStrings = ["25%", "50%", "75%", "100%"]; //We need to send events to Google Tag Manager on these percentages
    this.state = {
      isPlaying: false,
      isInitialPlay: true,
      videoTitle: null,
      videoProvider: this.getVideoProvider(this.props.videoUrl),
      playEvents: [false, false, false, false], // 25%, 50%, 75%, 100%
      canPlayVideo: ReactPlayer.canPlay(this.props.videoUrl),
      previousSeekPercent: 0,
    };
    this.statisticsCookieConsent = "0";
  }

  /**
   * Returns the video provider. [[VideoProvider]] is used to get the title of the video. Title is needed to send it to the Google Tag Manager.
   * @param videoUrl Link of the video which will be embedded
   */
  private getVideoProvider(videoUrl: string): VideoProvider {
    if (videoUrl.includes("youtu")) return VideoProvider.Youtube;
    if (videoUrl.includes("vimeo")) return VideoProvider.Vimeo;
    if (videoUrl.includes("screen9")) return VideoProvider.screen9;
    if (videoUrl.includes("qcnl")) return VideoProvider.screen9;
    if (videoUrl.includes("buster") || videoUrl.includes("jaws"))
      return VideoProvider.Buster;
    return VideoProvider.Undefined;
  }

  /**
   * Get the video title after the video is started playing.
   * Notice the early return if the method is triggered after the initial play.
   */
  onStartPlaying = () => {
    const { videoProvider, isInitialPlay } = this.state;
    if (!isInitialPlay) {
      return; //nothing to do here
    }

    const internalPlayer = this._videoRef.getInternalPlayer();
    this._videoDuration = this._videoRef.getDuration();
    let title = "";
    if (internalPlayer !== null && videoProvider === VideoProvider.Youtube) {
      const videoInfo = (internalPlayer as any).getVideoData();
      title = (videoInfo as any).title;
    } else if (
      internalPlayer !== null &&
      videoProvider === VideoProvider.Vimeo
    ) {
      const videoInfo: HTMLIFrameElement = (internalPlayer as any).element;
      title = videoInfo.title;
    }
    pushTagManagerEvent("video", "Video", title, "Play");

    this.setState({
      isPlaying: true,
      videoTitle: title,
      isInitialPlay: false,
    });
  };

  onPausePlaying = () => {
    this.setState({
      isPlaying: false,
    });
  };

  onVideoEnded = () => {
    this.handleUpdates(3);
  };

  /**
   * Set the playEvent array depending the progress percentage.
   */
  onProgressUpdate = (progress: ProgressState) => {
    const { previousSeekPercent } = this.state;
    const currentPercentage = progress.played;

    //user has rewinded, reset sent status
    if (previousSeekPercent > currentPercentage) {
      let event: boolean[];
      if (currentPercentage < 0.25) {
        event = [false, false, false, false];
      } else if (currentPercentage >= 0.25 && currentPercentage < 0.5) {
        event = [true, false, false, false];
      } else if (currentPercentage >= 0.5 && currentPercentage < 0.75) {
        event = [true, true, false, false];
      } else if (currentPercentage >= 0.75 && currentPercentage < 1) {
        event = [true, true, true, false];
      }
      this.setState({
        playEvents: event,
        previousSeekPercent: currentPercentage,
      });
    } else {
      this.setState({
        previousSeekPercent: currentPercentage,
      });
    }

    if (currentPercentage >= 0.25 && currentPercentage < 0.5) {
      this.handleUpdates(0);
    } else if (currentPercentage >= 0.5 && currentPercentage < 0.75) {
      this.handleUpdates(1);
    } else if (currentPercentage >= 0.75 && currentPercentage < 1) {
      this.handleUpdates(2);
    }
  };

  /**
   * Send the events for every true value in the array.
   */
  private handleUpdates = (index: number) => {
    const { playEvents, videoTitle } = this.state;
    const playEventsClone = playEvents.slice();

    while (!playEvents[index]) {
      console.log(videoTitle + "- " + this._percentageStrings[index]);
      pushTagManagerEvent(
        "video",
        "Video",
        videoTitle,
        this._percentageStrings[index]
      );
      playEventsClone[index] = true;
      if (--index === -1) break;
    }

    this.setState({
      isPlaying: index !== 3,
      playEvents: playEventsClone,
    });
  };
  onready = () => {
    const { blockId } = this.props;

    if (blockId) {
      let videoBlockWrapper = document.getElementById(blockId);
      let iframe =
        videoBlockWrapper && videoBlockWrapper.querySelector("iframe");

      let headingElement = document.getElementById(blockId + "_heading");
      let title = headingElement && headingElement.innerText;

      if (iframe && title) {
        iframe.setAttribute("title", title);
      }
    }
  };

  onConsentsStatistics = () => {
    return true;
  };
  componentDidMount() {
    EventService.subscribe(
      CookieConsentConstants.statisticsConsentEvent,
      this.onConsentsStatistics
    );
    this.statisticsCookieConsent = Cookies.get("consent_statistics");
  }
  public render() {
    const {
      busteriframetitle,
      screen9iframetitle,
      heading,
      videoUrl,
      description,
      defaultTextLang,
      blockId,
      campaign,
      anchorHeading,
    } = this.props;
    const { canPlayVideo } = this.state;
    let anchorHeadingId = anchorHeading ? "anchor_" : "";
    let iframeTitle =
      this.state.videoProvider == VideoProvider.Buster
        ? busteriframetitle
        : screen9iframetitle;
    let videoConfig = !isIOS()
      ? {
          youtube: {
            playerVars: { origin: window.location.origin },
          },
          vimeo: {
            playerOptions: {
              texttrack: defaultTextLang != null ? defaultTextLang : "none",
              dnt: this.statisticsCookieConsent === "1" ? false : true,
            },
          },
        }
      : {
          youtube: {
            playerVars: { origin: window.location.origin },
          },
          vimeo: {
            playerOptions: {
              dnt: this.statisticsCookieConsent === "1" ? false : true,
            },
          },
        };
    let attributes = {};
    if (!campaign && (heading || description)) {
      if (heading) {
        attributes["aria-labelledby"] = anchorHeadingId + blockId + "_heading";
      }
      if (description) {
        attributes["aria-describedby"] = blockId + "_desc";
      }
      attributes["role"] = "group";
    }

    return (
      <div {...attributes} className="video-block block-margin">
        {heading && !anchorHeading && (
          <h2 id={blockId + "_heading"}>{heading}</h2>
        )}
        {description && (
          <p id={blockId + "_desc"} className="video-block__description">
            {description}
          </p>
        )}
        {canPlayVideo && (
          <div className="player-wrapper">
            <ReactPlayer
              referrerPolicy="no-referrer-when-downgrade"
              ref={(ref) => (this._videoRef = ref)}
              url={videoUrl}
              controls={true}
              playing={false}
              progressInterval={1000}
              onStart={this.onStartPlaying}
              onPause={this.onPausePlaying}
              onEnded={this.onVideoEnded}
              onProgress={this.onProgressUpdate}
              onReady={this.onready}
              playsinline={true}
              id={blockId}
              config={videoConfig}
              width="100%"
              height="100%"
              className="react-player"
            />
          </div>
        )}
        {this.state.videoProvider == VideoProvider.Buster ||
          (this.state.videoProvider == VideoProvider.screen9 && (
            <div className="player-wrapper">
              <div className="react-player player-buster">
                <div className="player-buster__wrapper">
                  <iframe
                    referrerPolicy="no-referrer-when-downgrade"
                    src={videoUrl}
                    allowFullScreen
                    allow="accelerometer; autoplay; encrypted-media; gyroscope; picture-in-picture"
                    frameBorder="0"
                    title={iframeTitle}
                    width="100%"
                    height="100%"
                  />
                </div>
              </div>
            </div>
          ))}
        {!canPlayVideo &&
          this.state.videoProvider !== VideoProvider.Buster &&
          this.state.videoProvider !== VideoProvider.screen9 && (
            <p>Denna videolänk stöds inte.</p>
          )}{" "}
        {/* A fallback in case someone adds an url which is not supported. */}
      </div>
    );
  }
}
